import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@/components/ui';
import type { HierarchyPageVM } from './useHierarchyPage';
import hz from './Hierarchy.module.css';
import styles from './OrgDeactivateDialog.module.css';

export interface OrgDeactivateDialogProps {
    vm: HierarchyPageVM;
}

/**
 * EAA-1818: frame 32's first half — `Ngưng hoạt động "X"?`.
 *
 * WIRED TO `DELETE /organizations/{id}`, which is a SOFT deactivate: portal-api
 * flips `is_active` and never deletes the row, because Level, OrganizationAdmin
 * and Assignment rows all FK the organization. That is why every consequence
 * below is reversible and why the fourth bullet can promise it.
 *
 * THE ACCOUNT FIGURE, honestly sourced. The mock shows a static "124". The
 * authoritative count (`accounts_affected`, distinct Assignment +
 * OrganizationAdmin subjects) exists only in the DELETE *response*, so it cannot
 * be shown before the action. This dialog therefore states the org's active
 * assignment count from `provisioning-status` — a real pre-flight number, and
 * labelled as that rather than as the affected total — and the success toast
 * reports the exact `accounts_affected` the server returned.
 *
 * Super-admin only: rendered behind `canManageTree` (= `console.orgs.manage`,
 * issued to super-admins alone), matching the server's own gate. The note about
 * 404-vs-403 is the design's, and it is accurate — an org the caller cannot
 * administer answers NOT_FOUND so ids do not leak, while their OWN org answers
 * AUTH_PERMISSION_DENIED.
 */
export function OrgDeactivateDialog({ vm }: OrgDeactivateDialogProps) {
    const { t } = useTranslation();
    const {
        confirmDeactivateNode,
        setConfirmDeactivateOrgId,
        deactivatePreflightQuery,
        deactivateOrg,
    } = vm;

    const activeAccounts = deactivatePreflightQuery.data?.assignments.active ?? null;
    const name = confirmDeactivateNode?.organization.name ?? '';

    const accountsBullet =
        activeAccounts === null
            ? t('console.hierarchy.deactivateDialog.accountsBulletUnknown')
            : t('console.hierarchy.deactivateDialog.accountsBulletKnown', {
                  value: activeAccounts.toLocaleString('vi-VN'),
              });

    return (
        <Modal
            open={Boolean(confirmDeactivateNode)}
            onCancel={() => setConfirmDeactivateOrgId(null)}
            title={
                confirmDeactivateNode
                    ? t('console.hierarchy.deactivateDialog.title', { name })
                    : ''
            }
            footer={
                <div className="hz-move-footer">
                    <span className="hz-move-footer-note">
                        {t('console.hierarchy.auditLogNote')}
                    </span>
                    {/* EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`
                        — see AccountDetailModal.tsx for the mapping rationale. */}
                    <span className="hz-move-footer-actions">
                        <Button
                            onClick={() => setConfirmDeactivateOrgId(null)}
                            disabled={deactivateOrg.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            danger
                            disabled={deactivateOrg.isPending || !confirmDeactivateNode}
                            onClick={() => {
                                if (confirmDeactivateNode)
                                    deactivateOrg.mutate(confirmDeactivateNode.id);
                            }}
                        >
                            {deactivateOrg.isPending
                                ? t('console.hierarchy.processingLabel')
                                : t('console.hierarchy.deactivateDialog.confirmButton')}
                        </Button>
                    </span>
                </div>
            }
        >
            <p className={hz.cardNote}>{t('console.hierarchy.deactivateDialog.description')}</p>

            <ul className={styles.consequences}>
                <li className={`${styles.consequence} ${styles.consequenceWarn}`}>{accountsBullet}</li>
                <li className={`${styles.consequence} ${styles.consequenceWarn}`}>
                    {t('console.hierarchy.deactivateDialog.consequenceNoNewAccounts')}
                </li>
                <li className={`${styles.consequence} ${styles.consequenceWarn}`}>
                    {t('console.hierarchy.deactivateDialog.consequenceNoIncomingMoves')}
                </li>
                <li className={`${styles.consequence} ${styles.consequenceOk}`}>
                    {t('console.hierarchy.deactivateDialog.consequenceReversible')}
                </li>
            </ul>

            <p className={`${hz.cardNote} ${hz.deactivateNote}`}>
                {t('console.hierarchy.deactivateDialog.orgAdminNote')}
            </p>
        </Modal>
    );
}

export default OrgDeactivateDialog;
